import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Server, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { getAuthHeaders, getUser } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SFTPConfig = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [configs, setConfigs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    sftp_host: '',
    sftp_port: 22,
    sftp_username: '',
    sftp_password: '',
    upload_path: '/tracking',
    file_format: 'xml',
    update_frequency_minutes: 5
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/dispatcher/login');
      return;
    }
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/sftp/config`, { headers: getAuthHeaders() });
      setConfigs(response.data.configs || []);
    } catch (error) {
      console.error('Error loading configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/sftp/config`, formData, { headers: getAuthHeaders() });
      setShowForm(false);
      setFormData({
        customer_name: '',
        sftp_host: '',
        sftp_port: 22,
        sftp_username: '',
        sftp_password: '',
        upload_path: '/tracking',
        file_format: 'xml',
        update_frequency_minutes: 5
      });
      loadConfigs();
    } catch (error) {
      console.error('Error creating config:', error);
      alert('Failed to create SFTP configuration');
    }
  };

  const handleDelete = async (configId) => {
    if (!confirm('Delete this SFTP configuration?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/sftp/config/${configId}`, { headers: getAuthHeaders() });
      loadConfigs();
    } catch (error) {
      console.error('Error deleting config:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/dispatcher/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">SFTP Integration</h1>
              <p className="text-sm text-slate-600">Configure customer tracking integrations</p>
            </div>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-slate-800 hover:bg-slate-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Configuration
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Info Card */}
        <Card className="p-6 mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-start space-x-4">
            <Globe className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">How SFTP Integration Works</h3>
              <p className="text-sm text-blue-800 mb-2">
                Configure SFTP settings for each customer who wants real-time driver tracking on their systems.
              </p>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li>System automatically sends tracking updates to customer's SFTP server</li>
                <li>Supports XML, CSV, JSON, and EDI formats</li>
                <li>Updates sent every 5 minutes (configurable)</li>
                <li>Includes driver location, job status, and ETAs</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Configurations List */}
        {loading ? (
          <div className="text-center py-12">
            <Server className="h-12 w-12 mx-auto mb-4 text-slate-300 animate-pulse" />
            <p className="text-slate-600">Loading configurations...</p>
          </div>
        ) : configs.length === 0 ? (
          <Card className="p-12 text-center">
            <Server className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Configurations Yet</h3>
            <p className="text-slate-600 mb-6">Add your first SFTP configuration to enable customer tracking integrations.</p>
            <Button onClick={() => setShowForm(true)} className="bg-slate-800 hover:bg-slate-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Configuration
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {configs.map((config) => (
              <Card key={config.id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">{config.customer_name || config.customer_id}</h3>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        config.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {config.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold uppercase">
                        {config.file_format}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(config.id)}
                    className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">SFTP Host:</span>
                    <span className="font-mono text-slate-900">{config.sftp_host}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Port:</span>
                    <span className="font-mono text-slate-900">{config.sftp_port}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Username:</span>
                    <span className="font-mono text-slate-900">{config.sftp_username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Upload Path:</span>
                    <span className="font-mono text-slate-900">{config.upload_path}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Update Frequency:</span>
                    <span className="text-slate-900">{config.update_frequency_minutes} minutes</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Configuration Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8 p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Add SFTP Configuration</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Customer Name *</label>
                <Input
                  required
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  placeholder="Customer Company Name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">SFTP Host *</label>
                  <Input
                    required
                    value={formData.sftp_host}
                    onChange={(e) => setFormData({...formData, sftp_host: e.target.value})}
                    placeholder="sftp.customer.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Port *</label>
                  <Input
                    required
                    type="number"
                    value={formData.sftp_port}
                    onChange={(e) => setFormData({...formData, sftp_port: parseInt(e.target.value)})}
                    placeholder="22"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Username *</label>
                  <Input
                    required
                    value={formData.sftp_username}
                    onChange={(e) => setFormData({...formData, sftp_username: e.target.value})}
                    placeholder="username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password *</label>
                  <Input
                    required
                    type="password"
                    value={formData.sftp_password}
                    onChange={(e) => setFormData({...formData, sftp_password: e.target.value})}
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Upload Path</label>
                  <Input
                    value={formData.upload_path}
                    onChange={(e) => setFormData({...formData, upload_path: e.target.value})}
                    placeholder="/tracking"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">File Format</label>
                  <select
                    value={formData.file_format}
                    onChange={(e) => setFormData({...formData, file_format: e.target.value})}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white"
                  >
                    <option value="xml">XML</option>
                    <option value="csv">CSV</option>
                    <option value="json">JSON</option>
                    <option value="edi">EDI 214</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Update Frequency (minutes)</label>
                <Input
                  type="number"
                  value={formData.update_frequency_minutes}
                  onChange={(e) => setFormData({...formData, update_frequency_minutes: parseInt(e.target.value)})}
                  placeholder="5"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowForm(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-slate-800 hover:bg-slate-700"
                >
                  Add Configuration
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SFTPConfig;